import { IUser } from "@/interfaces/IUser";
import {
  BandWorkspace, Conversation, EventTypes, SlateEditorElement, SlateElementTypes, SlateParagraphElement,
  SlateUserMentionElement, TagOption
} from "@/types/types.ts";
import {
  PeaksInstanceType, WAVEFORM_ZOOMVIEW_TYPE_NAME, WaveformZoomviewType
} from "@/features/AudioWorkstation/utils/audio-editor-utils.ts";
import Konva from "konva";
import { PlayerAdapter } from "peaks.js";
import {
  Descendant, Editor, Element as SlateElement, Node as SlateNode, NodeEntry, Path, Text as SlateText, Transforms,
} from "slate";
import { isArray, isObject } from "lodash";
import { API_UPLOADS_PATH } from "@/app/api/constants.ts";
import { emitEvent } from "@/services/event-emitter.ts";
import Layer = Konva.Layer;

export const publicUrl = process.env.NEXT_PUBLIC_API_URL || "";

interface UrlBuilderOptions<T extends Record<string, string | number | boolean | null | undefined> = Record<string, never>> {
  params?: T;
  baseUrl?: string;
  hashtag?: string;
}

export const buildUrlWithParams = <T extends Record<string, string | number | boolean | null | undefined> = Record<string, never>>(
  options: UrlBuilderOptions<T> = {}
): string => {
  const { params = {} as T, baseUrl, hashtag } = options;
  const actualBaseUrl = baseUrl || (typeof window !== 'undefined' ?
    window.location.origin + window.location.pathname : '/');

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.set(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  const hashString = hashtag ? `#${hashtag}` : '';

  return `${actualBaseUrl}${queryString ? `?${queryString}` : ''}${hashString}`;
};

export const createConfirmAction = <T extends unknown[]>(
  fn: (...args: T) => void,
  ...args: T
) => () => fn(...args);

export const openConfirmDialog = (
  {
    title,
    message,
    onConfirm,
    cancelText,
    confirmText,
  }: {
    title?: string | React.ReactNode,
    message: string | React.ReactNode,
    onConfirm: () => void,
    cancelText?: string,
    confirmText?: string,
  }) => {
  emitEvent(EventTypes.OPEN_CONFIRM_DIALOG, {
    title: title || "Are you sure?",
    cancelText: cancelText || "Cancel",
    confirmText: confirmText || "Confirm",
    message: message,
    onConfirm,
  });
}

export type SnackbarData = {
  message: string | React.ReactNode;
  severity?: 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  icon?: React.ReactNode;
  user?: {
    email: string;
    avatar?: string;
  };
  track?: {
    name: string;
  };
};

export const openSnackbar = (data: SnackbarData) => {
  emitEvent(EventTypes.OPEN_SNACKBAR, {
    severity: data.severity || 'info',
    duration: data.duration || 4000,
    message: data.message,
    icon: data.icon,
    user: data.user,
    track: data.track,
  });
};

export function preventDefaultStopPropagation(event: MouseEvent | KeyboardEvent) {
  preventDefault(event)
  stopPropagation(event)
}

export function preventDefault(event: MouseEvent | KeyboardEvent) {
  event.preventDefault();
}

export function stopPropagation(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

export const safeVolume = (volume: number): number => {
  const bounded = Math.max(0, Math.min(1, volume));

  return Math.round(bounded * 10) / 10;
};

export const generateSlug = (name: string) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9- ]/g, '')
    .replace(/\s/g, '-')
    .replace(/-+/g, '-');
};

export const getCurrentDomain = () => {
  const domainParts = window.location.hostname.split('.');
  return domainParts.slice(-2).join('.');
};

export const calculateTextWidth = (text: string, font: string = '16px Roboto'): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;

  context.font = font;
  return context.measureText(text || '').width + 4;
};

export const safeParseSlateJSON = <T>(
  input: string | null | undefined,
  fallback?: string | object | [],
) => {
  try {
    const parsed = JSON.parse(input || '');
    // Optionally validate that the parsed object matches your expected structure
    if ((isArray(parsed) && parsed.length > 0) || (isObject(parsed) && Object.keys(parsed).length > 0)) {
      return parsed as T;
    }
    return (fallback || input) as T;
  } catch {
    return (fallback || input) as T;
  }
}

export const defaultSlateValue: Descendant[] = [
  {
    type: SlateElementTypes.PARAGRAPH,
    children: [{ text: '' }]
  } as SlateParagraphElement
];

export const getReplyConversation = (user: IUser): Conversation => {
  return {
    id: undefined,
    body: '[]',
    createdByUser: user,
    createdById: user?.id,
    createdByEmailAddress: user?.email,
  }
}
export const maxScrollTop = (dom: Element) => dom.scrollHeight - dom.clientHeight;

export const isEditorEmpty = (editor: Editor): boolean => {
  if (!editor.children || editor.children.length === 0) {
    return true;
  }

  return isEmptySlateElements(editor.children)
};

/*export const findSlateCaretCoordinates = (editor: Editor): { top: number; left: number } | null => {
  const { selection } = editor;

  if (!selection || !SlateRange.isCollapsed(selection)) {
    return null;
  }

  // Probeer de DOM selectie te verkrijgen
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount === 0) {
    return null;
  }

  const domRange = domSelection.getRangeAt(0).cloneRange();
  // Verplaats de range naar het editor element om de juiste coördinaten te krijgen
  domRange.collapse(true);
  const rect = domRange.getBoundingClientRect();

  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
  };
};*/

// TypeError: (0 , react__WEBPACK_IMPORTED_MODULE_3__.createContext) is not a function...
/*export const findSlateCaretCoordinates = (editor: DOMEditor) => {
  const {selection} = editor;
  if (!selection || !SlateRange.isCollapsed(selection)) return null;

  try {
    const domRange = ReactEditor.toDOMRange(editor, selection);
    const rect = domRange.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    };
  } catch (error) {
    console.error('Error getting caret coordinates:', error);
    return null;
  }
};*/

// https://github.com/ianstormtaylor/slate/issues/3858#issue-693108110
export const fixCannotFindDescendantSlateError = (slateEditor: Editor) => {
  Transforms.select(slateEditor, {
    anchor: {
      path: [
        0,
        0
      ],
      offset: 0
    },
    focus: {
      path: [
        0,
        0
      ],
      offset: 0
    },
  })
}

export const isEmptySlateElements = (elements: Descendant[]): boolean => {
  if (!elements) {
    return true;
  }

  return elements.every((node) => {
    // Check if the node has children
    if ('children' in node) {
      // Look for mentions in the children
      const hasMention = node.children.some(
        (child: Descendant) => {
          return 'type' in child && (
            child.type === SlateElementTypes.USER_MENTION
            || child.type === SlateElementTypes.BAND_MENTION
          )
        }
      );

      // If there's a mention, the editor is not empty
      if (hasMention) {
        return false;
      }
    }

    // If no mentions found, check if the text content is empty
    return SlateNode.string(node).trim() === '';
  });
};

export const findSlateElementPath = (editor: Editor, element: SlateElement) => {
  const [nodeEntry] = Editor.nodes(editor, {
    at: [],
    match: (n) => n === element,
  });

  if (nodeEntry) {
    const [, path] = nodeEntry;
    return path;
  }

  console.warn('Element not found in the editor:', element);
  return null;
};

export function isMentionNode(entry: NodeEntry<SlateEditorElement>): entry is [SlateUserMentionElement, Path] {
  const [node] = entry;
  return (node.type === SlateElementTypes.USER_MENTION || node.type === SlateElementTypes.BAND_MENTION)
    && !!node.mention;
}

export const extractMentionsFromSlateElements = (nodes: NodeEntry<SlateEditorElement>[]) => {
  return nodes
    .filter(isMentionNode)
    .map(([node]) => node.mention);
};

export const collectSlateNodesOfType = (
  editor: Editor,
  type: SlateElementTypes
): NodeEntry<SlateEditorElement>[] => {
  return Array.from(
    Editor.nodes(editor, {
      match: (node) =>
        !Editor.isEditor(node) &&
        (node as SlateEditorElement).type === type,
      at: [], // Doorzoek de hele editor
    })
  ) as NodeEntry<SlateEditorElement>[];
};

export const scrollToBottom = (
  element: HTMLElement | null,
  // options: ScrollToOptions = { behavior: 'smooth' }
) => {
  if (!element) return;

  element.scrollTop = element.scrollHeight;
};

/**
 * Functie om de editorinhoud schoon te maken door lege paragrafen aan het begin en einde te verwijderen.
 * Een paragraaf wordt als leeg beschouwd als alle kinderen lege tekstnodes zijn.
 * Paragrafen met andere elementen zoals MENTION blijven behouden.
 *
 * @param nodes - De huidige nodes van de editor.
 * @returns De schoongemaakte nodes.
 */
export const cleanupSlateData = (nodes: Descendant[]): Descendant[] => {
  // Als er geen nodes zijn, retourneer de standaard paragraaf
  if (nodes.length === 0) {
    return defaultSlateValue;
  }

  // Maak een kopie om de originele data niet te muteren
  const cleaned = [...nodes] as SlateEditorElement[];

  // Functie om te controleren of een node een lege paragraaf is
  const isEmptyParagraph = (node: SlateEditorElement): boolean => {
    if (!SlateElement.isElement(node)) return false;
    if (node.type !== SlateElementTypes.PARAGRAPH) return false;

    // Een paragraaf is leeg als alle kinderen lege tekstnodes zijn
    return node.children.every(child => {
      if (SlateText.isText(child)) {
        return child.text.trim() === "";
      }
      // Als er een niet-tekst kind is (zoals een MENTION), beschouw de paragraaf als niet leeg
      return false;
    });
  };

  // Verwijder alle lege paragrafen aan het begin (left trim)
  while (cleaned.length > 0 && isEmptyParagraph(cleaned[0])) {
    cleaned.shift();
  }

  // Verwijder alle lege paragrafen aan het einde (right trim)
  while (cleaned.length > 0 && isEmptyParagraph(cleaned[cleaned.length - 1])) {
    cleaned.pop();
  }

  // Zorg dat er altijd minimaal één paragraaf is
  if (cleaned.length === 0) {
    cleaned.push({
      type: SlateElementTypes.PARAGRAPH,
      children: [{ text: "" }],
    });
  }

  return cleaned;
};

export function handleFFMpegProgress(progressLine: string): Record<string, string | number> | null {
  // Remove all spaces after = and trim.
  // each progress part is a key-value pai. ex: "frame=123", "time=01:23:45.67"
  const progressParts: string[] = progressLine
    .replace(/=\s+/g, "=")
    .trim()
    .split(" ");

  // build progress object
  const progress: Record<string, string | number> = {};
  for (const keyValuePair of progressParts) {
    const [key, value] = keyValuePair.split("=", 2);
    if (typeof value === "undefined") {
      return null
    }
    const valueAsNumber = +value;
    progress[key] = !Number.isNaN(valueAsNumber) ? valueAsNumber : value;
  }

  // do something with progress object
  // console.log(progress);
  return progress;
}

export function generateTimestampedFilename(source: string) {
  const datePrefix = Date.now();
  const underscoreIndex = source.indexOf('_');

  if (underscoreIndex > -1) {
    // Verwijder alles voor en inclusief de eerste underscore
    return `${datePrefix}_${source.slice(underscoreIndex + 1)}`;
  }

  // Als er geen underscore is, gebruik de originele naam met de datum ervoor
  return `${datePrefix}_${source}`;
}

export const isZoomedOutFully = (zoomview: WaveformZoomviewType, player: PlayerAdapter): boolean => {
  const totalDuration = player.getDuration();
  const zoomviewWidth = zoomview._width;
  const visibleDuration = zoomview.pixelsToTime(zoomviewWidth);

  return Math.round(Math.abs(visibleDuration)) === Math.round(Math.abs(totalDuration));
};

/**
 * Returns the main domain for production environments or a development-specific hostname otherwise.
 * @param host - The host string (e.g., "sub.example.com", "example.com").
 * @returns The main domain or a development-specific hostname.
 */
export function getHostname(host: string): string {
  if (!host || typeof host !== "string") return "";

  // Check if the environment is development
  if (process.env.NODE_ENV === "development") {
    return getDevelopmentHostname(host);
  }

  // Normalize the host to lowercase and trim whitespace
  const normalizedHost = host.trim().toLowerCase();

  // Split the host into parts
  const parts = normalizedHost.split(".");

  // Ensure the domain ends with ".com"
  if (parts[parts.length - 1] !== "com") {
    return ""; // Invalid domain
  }

  // Return the last two parts as the main domain
  return parts.slice(-2).join(".");
}

/**
 * Handles development-specific hostname logic.
 * Checks for .local domains or localhost.
 * @param host - The host string (e.g., "sub.example.local", "localhost").
 * @returns The development-specific main domain or an empty string if invalid.
 */
function getDevelopmentHostname(host: string): string {
  if (process.env.NODE_ENV !== "development") {
    throw new Error(`Error: getDevelopmentHostname id not available in environment ${process.env.NODE_ENV}`);
  }

  if (!host || typeof host !== "string") {
    return "";
  }

  // Normalize the host to lowercase and trim whitespace
  const normalizedHost = host.trim().toLowerCase();

  // Special case for "localhost" (no TLD)
  if (normalizedHost === "localhost") {
    return "localhost";
  }

  // Split the host into parts
  const parts = normalizedHost.split(".");

  // Handle *.local domains
  if (parts.length >= 2 && parts[parts.length - 1] === 'local') {
    // Return the domain + TLD (example: wavuz.local)
    return parts.slice(-2).join(".");
  }

  // Handle *.com domains
  if (parts.length >= 2 && parts[parts.length - 1] === 'com') {
    // Return the domain + TLD (example: example.com)
    return parts.slice(-2).join(".");
  }

  // Handle single-word "localhost" without .local
  if (parts.length === 1 && parts[0] === "localhost") {
    return "localhost";
  }

  return ""; // Invalid host
}

export function getSubdomainName(host: string): string {
  if (!host || typeof host !== "string") return "";

  // Normalize the host to lowercase and trim whitespace
  const normalizedHost = host.trim().toLowerCase();

  // Split the host into parts
  const parts = normalizedHost.split(".");

  // Need at least 2 parts for any domain
  if (parts.length < 2) return "";

  // Handle special case for localhost
  if (parts.includes("localhost")) {
    return parts.length > 1 ? parts[0] : "";
  }

  return parts.slice(0, -2).join("-") || "";
}

export const getBandWorkspaceEventSource = (workspace?: BandWorkspace) => {
  if (!workspace) {
    return;
  }

  console.log('Initializing BandWorkspace EventSource');

  return new EventSource(`/.well-known/mercure?topic=/api/sse/workspace/${workspace?.id}`, {
    withCredentials: true,
  });
}

export const getUserEventSource = (user: IUser) => {
  console.log('Initializing User EventSource');

  return new EventSource(`/.well-known/mercure?topic=/api/sse/${user?.id}`, {
    withCredentials: true,
  });
}

export const expectsTrustedOrigins = (e: { origin: string }) => {
  const trustedOrigins = [
    "https://dubload.com",
    "https://dublift.com",
    "https://dublifter.com",
    "https://dublifters.com",
    "https://wavuz.com",
    "https://wavezlab.com",
    "https://bandedit.com",
    "https://artistedit.com",
    "https://whoaudio.com",
    "https://phonocity.com",
  ];

  const trustedWildcardOrigins = [
    /\.dubload\.com$/,
    /\.dublift\.com$/,
    /\.dublifter\.com$/,
    /\.dublifters\.com$/,
    /\.wavuz\.com$/,
    /\.wavezlab\.com$/,
    /\.bandedit\.com$/,
    /\.artistedit\.com$/,
    /\.whoaudio\.com$/,
    /\.phonocity\.com$/,
  ];

  // Voeg de dev-origin toe als de omgeving 'development' is
  if (process.env.NODE_ENV === 'development') {
    trustedOrigins.push(
      ...[
        "https://localhost",
        "https://dubload.local",
        "https://bandedit.local",
        "https://wavuz.local",
      ]);

    trustedWildcardOrigins.push(
      ...[
        /\.localhost$/,
        /\.bandedit.local$/,
        /\.dubload.local$/,
        /\.wavuz.local$/,
      ]
    )
  }

  if (trustedOrigins.includes(e.origin)) {
    return;
  }

  // Controleer wildcard matches met regex
  const originHostname = new URL(e.origin).hostname;
  const isTrustedWildcard = trustedWildcardOrigins.some((regex) =>
    regex.test(originHostname)
  );

  if (isTrustedWildcard) {
    return;
  }

  throw new Error("Origin error");
};

export function getAbsoluteOffsetLeft(element: HTMLElement) {
  let offsetLeft = 0;
  while (element) {
    offsetLeft += element.offsetLeft;
    element = element.offsetParent as HTMLElement;
  }
  return offsetLeft;
}

export function queryStringToObject(queryString: string): Record<string, string> {
  const pairs = queryString.split('&');
  const result: Record<string, string> = {};

  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=');
    // Decodeer URI-componenten en voeg ze toe aan het resultaatobject
    result[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }

  return result;
}

export function overlap(a: DOMRect, b: DOMRect) {
  return a.left <= b.right && b.left <= a.right && a.top <= b.bottom && b.top <= a.bottom
}

export function getEmptyArtwork(width: number, height: number) {
  if (typeof document === "undefined") {
    return
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

  ctx.fillStyle = 'rgba(100, 100, 100, .3)';

  ctx.fillRect(0, 0, width, height);

  return canvas;
}

export function getClientY(event: MouseEvent, target?: HTMLElement | null): number {
  if (!event.target && !target) {
    return 0
  }

  const element: HTMLElement = (target || event.target) as HTMLElement;
  if (!element?.tagName) {
    return 0
  }

  const rect: DOMRect = element.getBoundingClientRect();

  // remove the left offset width
  return event.clientY - rect.y
}

export function getClientX(event: MouseEvent, target?: HTMLElement): number {
  if (!event.target && !target) {
    return 0
  }

  const element: HTMLElement = (target || event.target) as HTMLElement;
  if (!element?.tagName) {
    return 0
  }

  const rect: DOMRect = element.getBoundingClientRect();

  // remove the left offset width
  return event.clientX - rect.x
}

export function findParentWithClass(element: Element | null, className: string): Element | null {
  while (element && !element.classList.contains(className)) {
    element = element.parentElement;
  }
  return element;
}

export const userHasLikedTrack = () => {
  console.log('@todo: implement')
}

export const getTagNames = (/*tags: TagOption[]*/) => {
  return [] //tags.map((tag: TagOption) => tag.title as string)
}

export const findTagOption = (option: string, tags: TagOption[]): TagOption => {
  const tagOption = tags.find((tagOption: TagOption) => tagOption.name === option)
  if (!tagOption?.tag_id) {
    //throw new Error(`Warning: tagOption "${option}" not found`)
  }

  return tagOption as TagOption
}

export const toggleAxisTimeInPeaksCanvasLayer = (peaks: PeaksInstanceType, value: boolean) => {
  const zoomview = peaks.views.getView(WAVEFORM_ZOOMVIEW_TYPE_NAME) as WaveformZoomviewType
  const layer = zoomview._axisLayer as Layer
  layer.setAttrs({ visible: value })
}

export async function downloadFile(fileUrl: string, fileName: string) {
  try {
    const response = await fetch(API_UPLOADS_PATH + '/' + fileUrl, {
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download error:', error);
  }
}

export const getCssVariable = (variableName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};

export const formatTime = (timeInSeconds: number): string => {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const roundUpToNextHundred = (num: number): number => {
  return Math.ceil(num / 100) * 100;
};


// Push notification service-worker logic
// https://medium.com/@ameerezae/implementing-web-push-notifications-in-next-js-a-complete-guide-e21acd89492d
export function notificationUnsupported(): boolean {
  let unsupported = false;
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('showNotification' in ServiceWorkerRegistration.prototype)
  ) {
    unsupported = true;
  }
  return unsupported;
}

/*async function submitSubscription(subscription: PushSubscription): Promise<void> {
  const endpointUrl = '/api/web-push/subscription';
  const res = await fetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ subscription }),
  });
  const result = await res.json();
}*/

export function detectBrowser(): string {
  const userAgent = navigator.userAgent;
  let browserName;
  let browserVersion = '';

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "Chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "Firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "Safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "Opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "Edge";
  } else {
    browserName = "Unknown";
  }

  // Probeer browser versie te krijgen
  const versionMatch = userAgent.match(/(chrome|firefox|safari|opr|edg(?=\/))\/?\s*(\d+)/i);
  if (versionMatch) {
    browserVersion = versionMatch[2];
  }

  return `${browserName} ${browserVersion}`.trim();
}

export function detectDevice(): string {
  const userAgent = navigator.userAgent;

  if (/ipad/i.test(userAgent)) {
    return 'iPad';
  } else if (/iphone/i.test(userAgent)) {
    return 'iPhone';
  } else if (/android/i.test(userAgent)) {
    return 'Android';
  } else if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  } else if (/macintosh|mac os x/i.test(userAgent)) {
    return 'Mac';
  } else if (/windows|win32/i.test(userAgent)) {
    return 'Windows';
  } else if (/linux/i.test(userAgent)) {
    return 'Linux';
  } else {
    return 'Unknown';
  }
}

export function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const updateWaveformViews = (peaksInstance: PeaksInstanceType) => {
  if (!peaksInstance) {
    return false;
  }

  try {
    const zoomView = peaksInstance.views.getView('zoomview');
    const overviewView = peaksInstance.views.getView('overview');
    const scrollbar = peaksInstance.views.getScrollbar();

    if (zoomView) {
      zoomView.fitToContainer();
    }

    if (overviewView) {
      overviewView.fitToContainer();
    }

    if (scrollbar) {
      scrollbar.fitToContainer();
    }

    return true;
  } catch (error) {
    console.error("Error updating waveform views:", error);
    return false;
  }
};

export const dispatchWindowResizeForPeaksReset = () => {
  // @note: this is a quick fix for resetting Peaks.js waveforms, when their container widths change.
  // The useCreateAudioEditorPeaksInstanceOnWindowResize hook listens for window resize events
  window.dispatchEvent(new Event('resize'));
}
